import React, { useEffect, useRef, useState } from "react";
import hrApi, { HR_TOKEN_KEY } from "./hrApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, Search, Plus, ArrowLeft, Upload, FileText, Trash2, Link2, Copy, ShieldCheck, LogOut, Users, FolderOpen, LayoutDashboard, CalendarDays, Download, Inbox, Siren, Building2, Clock, FileSignature, GitMerge, Bell, MessageSquare, Send, GraduationCap, BookMarked, AlertTriangle } from "lucide-react";
import { Dashboard, LeaveTab, EmpLeave, EmpGrievances, EmpIncidents, IncidentsRegister, CompanyDocs, TemplatesTab, DuplicatesTab, SendTemplate, SendOnboarding, ProfileCompleteness, hrDownload } from "./HrExtras";
import IntakeInbox from "./Confirmations";
import { AttendanceTab, EmpAttendance } from "./Attendance";
import { CleanupReview } from "./HrCleanup";
import { MyWeek } from "./HrHome";
import { EmployeesBoard } from "./HrEmployees";
import { splitName } from "./NameInputs";
import { NamesCheck } from "./NamesCheck";
import { SmsTemplatePicker } from "./SmsTemplatePicker";
import { Avatar, StatusPill, relTime, contractStatus, inductionStatus, visaStatus } from "./HrUi";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ReadyToSend from "./Outbox";
import { DistLists, MsgTemplates, FormTemplates } from "./OutboxAdmin";
import PdfTemplates from "./PdfTemplates";
import SwiAdmin from "./SwiAdmin";
import PoliciesAdmin from "./PoliciesAdmin";
import WarningsAdmin, { EmpWarningsTab } from "./WarningsAdmin";
import { EmpComms, NewStarterChecklist, ResignButton, GenerateForm } from "./Comms";

const NAV = [
  ["", [["dashboard", "My week", LayoutDashboard]]],
  ["People", [["employees", "Employees", Users], ["intake", "Intake & Confirmations", Inbox], ["outbox", "Ready to send", Send], ["warnings", "Warnings", AlertTriangle], ["dedupe", "Dedupe", GitMerge]]],
  ["Compliance", [["documents", "Documents", FolderOpen], ["templates", "Templates", FileSignature], ["training", "Training (SWI)", GraduationCap], ["policies", "Policies", BookMarked], ["incidents", "Incidents", Siren]]],
  ["Work", [["attendance", "Hours & Attendance", Clock], ["leave", "Leave", CalendarDays]]],
  ["Company", [["company", "Company", Building2]]],
];

function Sidebar({ tab, setTab, badges, onLogout }) {
  return (
    <aside className="hidden md:flex fixed inset-y-0 left-0 w-60 bg-slate-900 text-slate-300 flex-col z-30" data-testid="hr-sidebar">
      <div className="px-5 py-5 flex items-center gap-2 text-white"><span className="w-8 h-8 rounded-xl bg-indigo-500 grid place-items-center"><Lock size={15} /></span><span className="font-heading font-extrabold">HR Portal</span></div>
      <nav className="flex-1 overflow-y-auto px-3 space-y-4" data-testid="hr-nav">
        {NAV.map(([group, items]) => (
          <div key={group || "top"}>
            {group && <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 px-3 mb-1">{group}</div>}
            {items.map(([k, label, Icon]) => (
              <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${tab === k ? "bg-indigo-500 text-white" : "hover:bg-slate-800 hover:text-white"}`}>
                <Icon size={16} /><span className="flex-1 text-left">{label}</span>
                {badges[k] > 0 && <span data-testid={`badge-${k}`} className={`text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 grid place-items-center ${k === "intake" || k === "outbox" ? "bg-rose-500 text-white" : "bg-slate-700 text-slate-200"}`}>{badges[k]}</span>}
              </button>
            ))}
          </div>
        ))}
      </nav>
      <button data-testid="hr-logout-side" onClick={onLogout} className="m-3 flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-slate-800"><LogOut size={15} /> Lock portal</button>
    </aside>
  );
}

function MobileNav({ tab, setTab, badges }) {
  const items = [["dashboard", "Home", LayoutDashboard], ["intake", "Intake", Inbox], ["employees", "Employees", Users], ["search", "Search", Search]];
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 grid grid-cols-4 pb-[env(safe-area-inset-bottom)]" data-testid="mobile-nav">
      {items.map(([k, label, Icon]) => (
        <button key={k} data-testid={`mnav-${k}`} onClick={() => setTab(k)} className={`relative py-2.5 flex flex-col items-center text-[11px] font-semibold ${tab === k || (k === "search" && tab === "employees-search") ? "text-indigo-600" : "text-slate-500"}`}>
          <Icon size={20} />{label}
          {k === "intake" && badges.intake > 0 && <span className="absolute top-1 right-[30%] bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] grid place-items-center">{badges.intake}</span>}
        </button>
      ))}
    </nav>
  );
}

function NavBar({ tab, setTab }) {
  return (
    <nav className="md:hidden flex overflow-x-auto gap-1 pb-1 -mx-1">
      {NAV.flatMap(([, items]) => items).map(([k, label, Icon]) => (
        <button key={k} onClick={() => setTab(k)} className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${tab === k ? "bg-indigo-500 text-white" : "bg-white text-slate-500 border border-slate-200"}`}><Icon size={13} /> {label}</button>
      ))}
    </nav>
  );
}

function DocumentsTable({ docs }) {
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("SHA-256 copied"); };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto" data-testid="docs-table">
      <table className="w-full text-sm">
        <thead className="text-xs text-slate-500 bg-slate-50 text-left"><tr><th className="p-3">File</th><th className="p-3">Employee</th><th className="p-3">Type</th><th className="p-3">Date</th><th className="p-3">Status</th><th className="p-3">SHA-256</th><th className="p-3"></th></tr></thead>
        <tbody>
          {docs.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400" data-testid="docs-empty">No documents match.</td></tr>}
          {docs.map((d) => (
            <tr key={d.id} data-testid={`gdoc-${d.id}`} className="border-t border-slate-50 hover:bg-slate-50/60">
              <td className="p-3 max-w-[260px]"><button onClick={() => hrOpen(`/documents/${d.id}/file`)} className="text-left text-slate-800 font-medium truncate block max-w-full hover:underline" title={d.filename}>{d.filename}</button></td>
              <td className="p-3 text-slate-600 whitespace-nowrap">{d.employee_name}</td>
              <td className="p-3"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 whitespace-nowrap" data-testid={`gdoc-type-${d.id}`}>{docLabel(d.doc_type)}</span></td>
              <td className="p-3 text-slate-500 font-num whitespace-nowrap">{new Date(d.signed_at || d.uploaded_at).toLocaleDateString("en-AU")}</td>
              <td className="p-3 text-xs whitespace-nowrap">{d.signed ? <StatusPill status={d.superseded ? "superseded" : "signed"}>{d.superseded ? `Superseded v${d.version}` : `Signed v${d.version}`}</StatusPill> : d.received || d.immutable ? <StatusPill status="opened">Locked</StatusPill> : <StatusPill status="draft">Uploaded</StatusPill>}</td>
              <td className="p-3">{d.sha256 ? <button onClick={() => copy(d.sha256)} title={d.sha256} data-testid={`gdoc-hash-${d.id}`} className="font-mono text-[11px] text-slate-500 hover:text-indigo-600">{d.sha256.slice(0, 12)}…</button> : <span className="text-slate-300">—</span>}</td>
              <td className="p-3"><button data-testid={`gdoc-download-${d.id}`} onClick={() => hrOpen(`/documents/${d.id}/file?download=1`, d.filename)} className="text-slate-400 hover:text-indigo-600"><Download size={15} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
import { DOC_TYPES, docLabel, hrOpen, SigningStatus, EmpDocuments, ContractManagement, ContractCheck, AuditTrail, EmpActions, DeletionLog } from "./HrCompliance";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const STATUS = ["Full-time", "Part-time", "Casual", "Contractor"];
const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Link copied"); };
const ROLES = ["Line Operator", "Line Packer", "Machine Operator", "Forklift Driver", "Team Leader", "Supervisor", "Quality Control", "Store Person", "Cleaner", "Trainee", "Administration", "Manager", "Other"];
const VISA_TYPES = ["Australian Citizen", "Permanent Resident", "Subclass 482 (TSS)", "Subclass 485 (Graduate)", "Subclass 500 (Student)", "Subclass 417/462 (Working Holiday)", "Subclass 491/494 (Regional)", "Bridging Visa", "Other / N/A"];

// Stable, module-level field components (defining these inside a component causes inputs to lose focus on every keystroke).
function Field({ testid, label, value, type, onChange }) {
  return (
    <div><Label className="text-xs text-slate-500">{label}</Label>
      <Input data-testid={testid} type={type || "text"} value={value || ""} onChange={onChange} className="h-10 mt-1" /></div>
  );
}
function SelectField({ testid, label, value, onChange, options, placeholder }) {
  const opts = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <div><Label className="text-xs text-slate-500">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="h-10 mt-1"><SelectValue placeholder={placeholder || "Select…"} /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

// ---------- Login gate ----------
function HrLogin({ onOk }) {
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await hrApi.post("/auth/login", { passcode: pass });
      localStorage.setItem(HR_TOKEN_KEY, data.token);
      onOk();
    } catch (err) { toast.error(err.response?.data?.detail || "Wrong passcode"); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen grid place-items-center bg-slate-900 p-4" data-testid="hr-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-slate-800 rounded-3xl p-8 shadow-2xl border border-slate-700">
        <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 grid place-items-center mb-4"><Lock className="text-indigo-300" size={22} /></div>
        <h1 className="font-heading font-extrabold text-2xl text-white">HR Portal</h1>
        <p className="text-slate-400 text-sm mt-1 mb-5">Private &amp; confidential. Enter the HR passcode.</p>
        <Input data-testid="hr-passcode" type="password" autoFocus value={pass} onChange={(e) => setPass(e.target.value)}
          placeholder="Passcode" className="h-12 bg-slate-900 border-slate-700 text-white rounded-xl" />
        <Button data-testid="hr-login-submit" disabled={busy || !pass} className="w-full h-12 mt-4 bg-indigo-500 hover:bg-indigo-400 text-white rounded-xl font-bold">
          {busy ? "Checking…" : "Enter"}
        </Button>
      </form>
    </div>
  );
}

// ---------- Employee detail ----------
function EmployeeDetail({ id, onBack, onOpen }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [linkDlg, setLinkDlg] = useState(null); // "contract" | "onboarding"
  const [ctrDoc, setCtrDoc] = useState("none");
  const [ctrText, setCtrText] = useState("");
  const [madeLink, setMadeLink] = useState(null);
  const [pTab, setPTab] = useState("overview");
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsBody, setSmsBody] = useState("Hi, this is G&L One HR. — G&L One");
  const load = () => hrApi.get(`/employees/${id}`).then((r) => {
    setData(r.data);
    const e = r.data.employee; const s = splitName(e.name);
    setForm({ ...e, first_name: e.first_name || s.first, family_name: e.family_name || s.last });
  });
  useEffect(() => { load(); }, [id]);
  if (!data || !form) return <div className="max-w-5xl mx-auto space-y-3"><div className="h-28 rounded-3xl bg-slate-200/70 animate-pulse" /><div className="h-10 rounded-xl bg-slate-200/70 animate-pulse" /><div className="h-64 rounded-2xl bg-slate-200/70 animate-pulse" /></div>;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const { data } = await hrApi.put(`/employees/${id}`, form);
      if (data.warning) toast.warning(data.warning, { duration: 10000 }); else toast.success("Profile saved");
      load();
    } catch (e) { toast.error(`Profile NOT saved — ${e.response?.data?.detail || e.message || "please try again"}`, { duration: 10000 }); }
  };
  const makeContract = async () => {
    const { data: l } = await hrApi.post(`/employees/${id}/contract`, { document_id: ctrDoc === "none" ? null : ctrDoc, contract_text: ctrText });
    setMadeLink(`${window.location.origin}/hr/sign/${l.token}`); load();
  };
  const makeOnboarding = async () => {
    const { data: l } = await hrApi.post(`/employees/${id}/onboarding`, {});
    setMadeLink(`${window.location.origin}/hr/onboard/${l.token}`); load();
  };

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="employee-detail">
      <button onClick={onBack} data-testid="emp-back" className="inline-flex items-center gap-1.5 text-slate-500 text-sm hover:text-slate-800"><ArrowLeft size={16} /> All employees</button>
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 flex flex-col lg:flex-row lg:items-center gap-4" data-testid="profile-header">
        <Avatar name={form.name} size={60} />
        <div className="flex-1 min-w-0">
          <h1 className="font-heading font-extrabold text-2xl text-slate-800 break-words" data-testid="profile-name">{form.name}</h1>
          <p className="text-slate-500 text-sm">{[form.role, form.department, form.employment_status].filter(Boolean).join(" · ") || "No role set yet"}</p>
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="profile-pills">
            <StatusPill status={contractStatus(data.employee)[0]} testid="pill-contract">Contract: {contractStatus(data.employee)[1]}</StatusPill>
            <StatusPill status={inductionStatus(data.employee)[0]} testid="pill-induction">Induction: {inductionStatus(data.employee)[1]}</StatusPill>
            <StatusPill status={visaStatus(data.employee)[0]} testid="pill-visa">Visa: {visaStatus(data.employee)[1]}</StatusPill>
            <StatusPill status={data.employee.onboarding_completed ? "signed" : "awaiting"} testid="pill-onboarding">{data.employee.onboarding_completed ? "Onboarded" : "Onboarding open"}</StatusPill>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="profile-sms" onClick={() => setSmsOpen(true)} className="gap-1.5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white"><MessageSquare size={15} /> SMS</Button>
          <Button data-testid="profile-send-onboarding" variant="outline" onClick={() => setPTab("contracts")} className="gap-1.5 rounded-full"><Send size={15} /> Send onboarding</Button>
          <ResignButton id={id} name={form.name} onDone={load} />
          <EmpActions id={id} name={form.name || "employee"} onDeleted={onBack} />
        </div>
      </div>
      <Dialog open={smsOpen} onOpenChange={setSmsOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="profile-sms-dialog"><DialogHeader><DialogTitle>SMS {form.name}</DialogTitle><DialogDescription className="text-xs">To {form.mobile || "no mobile on file"} · logged in the audit trail</DialogDescription></DialogHeader>
          <SmsTemplatePicker onPick={setSmsBody} />
          <Textarea data-testid="profile-sms-body" value={smsBody} onChange={(e) => setSmsBody(e.target.value)} rows={4} />
          <DialogFooter><Button variant="outline" onClick={() => setSmsOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="profile-sms-send" onClick={async () => { try { const { data: r } = await hrApi.post(`/employees/${id}/sms`, { body: smsBody }); r.ok ? toast.success(`SMS sent ✓${r.note ? ` (${r.note})` : ""}`) : toast.error(r.error || "SMS failed"); setSmsOpen(false); load(); } catch (e) { toast.error(e.response?.data?.detail || "SMS failed"); } }} className="rounded-full bg-indigo-500 text-white">Send SMS</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile */}
      <Tabs value={pTab} onValueChange={setPTab} className="w-full">
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none p-0 h-auto w-full justify-start overflow-x-auto gap-1" data-testid="profile-tabs">
          {[["overview", "Overview"], ["documents", `Documents (${data.documents.length})`], ["contracts", "Contracts"], ["comms", "Communications"], ["hours", "Hours"], ["leave", "Leave"], ["warnings", `Warnings (${(data.warnings || []).length})`], ["incidents", "Incidents"], ["audit", "Audit"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`ptab-${v}`} className="rounded-none border-b-2 border-transparent px-3 py-2 text-sm data-[state=active]:border-indigo-500 data-[state=active]:text-indigo-600 data-[state=active]:shadow-none">{l}</TabsTrigger>))}
        </TabsList>
        <TabsContent value="overview" className="space-y-5 mt-4">
      <NewStarterChecklist employee={data.employee} />
      <SigningStatus employee={data.employee} documents={data.documents} reload={load} />
      <ProfileCompleteness employee={form} documents={data.documents} />
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <h3 className="font-heading font-bold text-slate-800 mb-3">Profile</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field testid="emp-first_name" label="First name" value={form.first_name} onChange={(e) => setF("first_name", e.target.value)} />
          <Field testid="emp-family_name" label="Last name" value={form.family_name} onChange={(e) => setF("family_name", e.target.value)} />
          <Field testid="emp-kronos" label="Kronos ID" value={form.kronos} onChange={(e) => setF("kronos", e.target.value)} />
          <Field testid="emp-preferred_name" label="Preferred name" value={form.preferred_name} onChange={(e) => setF("preferred_name", e.target.value)} />
          <Field testid="emp-dob" label="Date of birth" type="date" value={form.dob} onChange={(e) => setF("dob", e.target.value)} />
          <Field testid="emp-email" label="Personal email" value={form.email} onChange={(e) => setF("email", e.target.value)} />
          <div><Field testid="emp-mobile" label="Mobile" value={form.mobile} onChange={(e) => setF("mobile", e.target.value)} />
            {form.mobile && !/^(04\d{8}|\+?614\d{8}|\+\d{9,15})$/.test(String(form.mobile).replace(/[\s()-]/g, "")) && <div className="text-[11px] text-rose-600 mt-1" data-testid="emp-mobile-warning">This number looks wrong ({String(form.mobile).replace(/\D/g, "").length} digits) — SMS won't send. Use 04xx xxx xxx.</div>}</div>
          <Field testid="emp-start_date" label="Start date" type="date" value={form.start_date} onChange={(e) => setF("start_date", e.target.value)} />
          <SelectField testid="emp-employment_status" label="Employment type" value={form.employment_status} onChange={(v) => setF("employment_status", v)} options={STATUS} />
          <SelectField testid="emp-role" label="Role / position" value={form.role} onChange={(v) => setF("role", v)} options={ROLES} />
          <Field testid="emp-department" label="Department" value={form.department} onChange={(e) => setF("department", e.target.value)} />
          <Field testid="emp-address" label="Address" value={form.address} onChange={(e) => setF("address", e.target.value)} />
          <Field testid="emp-emergency_contact" label="Emergency contact" value={form.emergency_contact} onChange={(e) => setF("emergency_contact", e.target.value)} />
          <SelectField testid="emp-visa_type" label="Visa type" value={form.visa_type} onChange={(v) => setF("visa_type", v)} options={VISA_TYPES} />
          <Field testid="emp-visa_expiry" label="Visa / RTW expiry" type="date" value={form.visa_expiry} onChange={(e) => setF("visa_expiry", e.target.value)} />
          <SelectField testid="emp-id_type" label="Photo ID type" value={form.id_type} onChange={(v) => setF("id_type", v)} options={["Driver licence", "Passport"]} />
          <Field testid="emp-id_number" label="Licence / passport number" value={form.id_number} onChange={(e) => setF("id_number", e.target.value)} />
          <Field testid="emp-pay_rate" label="Pay rate" value={form.pay_rate} onChange={(e) => setF("pay_rate", e.target.value)} />
          <Field testid="emp-pay_basis" label="Pay basis (hr/yr)" value={form.pay_basis} onChange={(e) => setF("pay_basis", e.target.value)} />
        </div>
        <div className="mt-3"><Label className="text-xs text-slate-500">Notes</Label><Textarea data-testid="emp-notes" value={form.notes || ""} onChange={(e) => setF("notes", e.target.value)} className="mt-1" /></div>
        <Button data-testid="emp-save" onClick={save} className="mt-3 bg-indigo-500 hover:bg-indigo-400 text-white rounded-full">Save profile</Button>
      </div>
        </TabsContent>
        <TabsContent value="documents" className="space-y-5 mt-4">
      <EmpDocuments id={id} data={data} reload={load} />
        </TabsContent>
        <TabsContent value="comms" className="space-y-5 mt-4">
      <EmpComms id={id} onOpen={onOpen} />
        </TabsContent>
        <TabsContent value="hours" className="space-y-5 mt-4">
      <EmpAttendance id={id} data={data} reload={load} />
        </TabsContent>
        <TabsContent value="leave" className="space-y-5 mt-4">
      <EmpLeave id={id} data={data} reload={load} />
        </TabsContent>
        <TabsContent value="incidents" className="space-y-5 mt-4">
      <EmpIncidents id={id} data={data} reload={load} />
      <EmpGrievances id={id} data={data} reload={load} />
        </TabsContent>
        <TabsContent value="warnings" className="space-y-5 mt-4">
      <EmpWarningsTab id={id} data={data} reload={load} />
        </TabsContent>
        <TabsContent value="contracts" className="space-y-5 mt-4">
      <SendOnboarding id={id} reload={load} />
      <ContractManagement id={id} name={form.name} employee={data.employee} links={data.links} reload={load} />
      <SendTemplate id={id} reload={load} />
      <GenerateForm id={id} reload={load} />
      <Button data-testid="gen-onboarding" variant="outline" onClick={() => { setLinkDlg("onboarding"); setMadeLink(null); }} className="gap-1.5 rounded-full"><Link2 size={15} /> Legacy onboarding link</Button>
        </TabsContent>
        <TabsContent value="audit" className="space-y-5 mt-4">
      <AuditTrail id={id} audit={data.audit} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!linkDlg} onOpenChange={(o) => !o && setLinkDlg(null)}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader><DialogTitle className="capitalize">{linkDlg} link</DialogTitle>
            <DialogDescription className="text-xs">Generate a unique web link to send to {form.name}. In this phase you copy &amp; send it yourself.</DialogDescription></DialogHeader>
          {!madeLink ? (
            linkDlg === "contract" ? (
              <div className="space-y-3">
                <div><Label className="text-xs text-slate-500">Attach a contract document (optional)</Label>
                  <Select value={ctrDoc} onValueChange={setCtrDoc}>
                    <SelectTrigger data-testid="ctr-doc" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None — text only</SelectItem>
                      {data.documents.filter((d) => ["contract", "award_rate", "other"].includes(d.doc_type)).map((d) => <SelectItem key={d.id} value={d.id}>{d.filename}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs text-slate-500">Or contract text to display</Label>
                  <Textarea data-testid="ctr-text" value={ctrText} onChange={(e) => setCtrText(e.target.value)} rows={4} placeholder="Paste the contract terms the employee should read & sign…" className="mt-1" /></div>
                <Button data-testid="ctr-create" onClick={makeContract} className="w-full bg-indigo-500 hover:bg-indigo-400 text-white rounded-full">Generate signing link</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">The employee will complete their personal details, upload ID / visa / right-to-work docs, and acknowledge induction. Everything files against this record.</p>
                <Button data-testid="onb-create" onClick={makeOnboarding} className="w-full bg-indigo-500 hover:bg-indigo-400 text-white rounded-full">Generate onboarding link</Button>
              </div>
            )
          ) : (
            <div className="space-y-2" data-testid="made-link">
              <p className="text-sm text-slate-600">Send this link to {form.name}:</p>
              <div className="flex gap-2"><Input readOnly value={madeLink} className="h-10 text-xs" /><Button onClick={() => copy(madeLink)} className="rounded-xl"><Copy size={15} /></Button></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setLinkDlg(null)} className="rounded-full">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Employees list + Documents search ----------
function Portal({ onLogout }) {
  const [tab, setTabRaw] = useState("dashboard");
  const [badges, setBadges] = useState({});
  const [last, setLast] = useState({});
  const [empLoading, setEmpLoading] = useState(true);
  const searchRef = useRef(null);
  const setTab = (k) => { setSelIdRaw(null); if (k === "search") { setTabRaw("employees"); setTimeout(() => searchRef.current?.focus(), 50); } else setTabRaw(k); };
  const loadBadges = () => hrApi.get("/overview").then((r) => { setBadges({ intake: r.data.needs_confirmation, outbox: r.data.ready_to_send, leave: r.data.pending_leave, incidents: r.data.open_incidents }); setLast(r.data.last_activity || {}); }).catch(() => {});
  useEffect(() => { loadBadges(); const t = setInterval(loadBadges, 30000); return () => clearInterval(t); }, []);
  const [q, setQ] = useState("");
  const [emps, setEmps] = useState([]);
  const [selId, setSelIdRaw] = useState(null);
  const setSelId = (id) => { setSelIdRaw(id); window.scrollTo(0, 0); };
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState({ first: "", last: "" });
  const [docs, setDocs] = useState([]);
  const [docQ, setDocQ] = useState("");
  const [docFilter, setDocFilter] = useState("all");
  const loadEmps = () => hrApi.get("/employees", { params: q ? { q } : {} }).then((r) => { setEmps(r.data); setEmpLoading(false); });
  const [importing, setImporting] = useState(false);
  const importManpower = async () => {
    setImporting(true);
    try { const { data } = await hrApi.post("/import-manpower"); toast.success(`Imported ${data.added} new (${data.skipped} already existed) — ${data.total} total`); loadEmps(); }
    catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setImporting(false);
  };
  const docReq = useRef(0);
  const loadDocs = () => {
    const n = ++docReq.current;
    hrApi.get("/documents", { params: { doc_type: docFilter === "all" ? undefined : docFilter, q: docQ || undefined } })
      .then((r) => { if (n === docReq.current) setDocs(r.data); });
  };
  useEffect(() => { loadEmps(); }, [q]);
  useEffect(() => { if (tab === "documents") loadDocs(); }, [tab, docFilter, docQ]);

  const addEmp = async () => {
    if (!newName.first.trim() || !newName.last.trim()) return toast.error("Enter a first and last name");
    const { data } = await hrApi.post("/employees", { first_name: newName.first.trim(), family_name: newName.last.trim() });
    setAddOpen(false); setNewName({ first: "", last: "" }); setSelId(data.id); toast.success("Employee created");
  };
  const logout = async () => { try { await hrApi.post("/auth/logout"); } catch {} localStorage.removeItem(HR_TOKEN_KEY); onLogout(); };

  const shell = (content) => (
    <div className="min-h-screen bg-slate-50" data-testid="hr-portal">
      <Sidebar tab={selId ? "employees" : tab} setTab={setTab} badges={badges} onLogout={logout} />
      <div className="md:pl-60"><TopBar onLogout={logout} /><div className="max-w-6xl mx-auto p-4 md:p-8 space-y-4 pb-24 md:pb-8">{content}</div></div>
      <MobileNav tab={tab} setTab={setTab} badges={badges} />
    </div>
  );
  if (selId) return shell(<EmployeeDetail key={selId} id={selId} onOpen={(eid) => setSelId(eid)} onBack={() => { setSelIdRaw(null); loadEmps(); loadBadges(); }} />);

  return shell(
    <>
        <NavBar tab={tab} setTab={setTab} />

        {tab === "dashboard" && <MyWeek onOpen={(eid) => setSelId(eid)} onGoto={setTab} />}
        {tab === "leave" && <LeaveTab onOpen={(eid) => setSelId(eid)} />}
        {tab === "intake" && <IntakeInbox onChanged={loadBadges} />}
        {tab === "incidents" && <IncidentsRegister onOpen={(eid) => setSelId(eid)} />}
        {tab === "attendance" && <AttendanceTab onOpen={(eid) => setSelId(eid)} />}
        {tab === "outbox" && <ReadyToSend onOpen={(eid) => setSelId(eid)} onChanged={loadBadges} />}
        {tab === "training" && <SwiAdmin />}
        {tab === "policies" && <PoliciesAdmin />}
        {tab === "warnings" && <WarningsAdmin />}
        {tab === "templates" && <><ContractCheck onOpen={(eid) => setSelId(eid)} /><PdfTemplates /><MsgTemplates /><FormTemplates /><TemplatesTab /></>}
        {tab === "dedupe" && <><CleanupReview /><DuplicatesTab /></>}
        {tab === "company" && <><CompanyDocs /><DistLists /></>}

        {tab === "employees" && <>
          <NamesCheck onOpen={setSelId} reload={loadEmps} />
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 basis-full sm:basis-auto"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input ref={searchRef} data-testid="emp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or role…" className="pl-9 h-11 rounded-xl" /></div>
            <Button data-testid="emp-import" onClick={importManpower} disabled={importing} variant="outline" className="gap-1.5 rounded-xl h-11">{importing ? "Importing…" : "Import manpower"}</Button>
            <Button data-testid="emp-add" onClick={() => setAddOpen(true)} className="gap-1.5 bg-indigo-500 hover:bg-indigo-400 text-white rounded-xl h-11"><Plus size={16} /> Add employee</Button>
          </div>
          <EmployeesBoard emps={emps} loading={empLoading} last={last} onOpen={(eid) => setSelId(eid)} reload={loadEmps} onAdd={() => setAddOpen(true)} />
          <DeletionLog />
        </>}

        {tab === "documents" && <>
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="doc-search" value={docQ} onChange={(e) => setDocQ(e.target.value)} placeholder="Search file name…" className="pl-9 h-11 rounded-xl" /></div>
            <Select value={docFilter} onValueChange={setDocFilter}>
              <SelectTrigger data-testid="doc-filter" className="h-11 w-52 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All document types</SelectItem>{DOC_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="text-xs text-slate-400" data-testid="docs-count">{docs.length} document{docs.length === 1 ? "" : "s"}</div>
          <DocumentsTable docs={docs} />
        </>}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader><DialogTitle>Add employee</DialogTitle><DialogDescription className="text-xs">Create the record, then fill in details or send an onboarding link.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="new-emp-first" value={newName.first} onChange={(e) => setNewName({ ...newName, first: e.target.value })} placeholder="First name" className="h-11" />
            <Input data-testid="new-emp-last" value={newName.last} onChange={(e) => setNewName({ ...newName, last: e.target.value })} placeholder="Last name" className="h-11" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="new-emp-create" onClick={addEmp} className="bg-indigo-500 hover:bg-indigo-400 text-white rounded-full">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TopBar({ onLogout }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [acts, setActs] = useState([]);
  const loadCount = () => hrApi.get("/activity/unread-count").then((r) => setCount(r.data.count)).catch(() => {});
  useEffect(() => { loadCount(); const t = setInterval(loadCount, 20000); return () => clearInterval(t); }, []);
  const toggle = async () => {
    const nx = !open; setOpen(nx);
    if (nx) { const { data } = await hrApi.get("/activity"); setActs(data); try { await hrApi.post("/activity/mark-read"); setCount(0); } catch {} }
  };
  return (
    <div className="bg-slate-900 text-white px-4 md:px-6 py-3 flex items-center justify-between relative">
      <div className="flex items-center gap-2 font-heading font-bold"><Lock size={16} className="text-indigo-300" /> HR Portal <span className="text-xs text-slate-400 font-normal">· private</span></div>
      <div className="flex items-center gap-4">
        <button data-testid="activity-bell" onClick={toggle} className="relative text-slate-300 hover:text-white">
          <Bell size={18} />
          {count > 0 && <span data-testid="activity-unread" className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 grid place-items-center">{count}</span>}
        </button>
        <button data-testid="hr-logout" onClick={onLogout} className="text-slate-300 hover:text-white inline-flex items-center gap-1.5 text-sm"><LogOut size={15} /> Lock</button>
      </div>
      {open && (
        <div data-testid="activity-panel" className="absolute right-4 top-14 z-50 w-80 bg-white text-slate-800 rounded-2xl shadow-2xl border border-slate-100 p-3 max-h-96 overflow-y-auto">
          <div className="font-heading font-bold text-sm mb-2">Activity</div>
          {acts.length === 0 ? <div className="text-sm text-slate-400">Nothing yet.</div> : acts.map((a) => (
            <div key={a.id} className="text-sm text-slate-700 border-b border-slate-50 pb-1.5 mb-1.5 last:border-0">{a.text}<div className="text-[11px] text-slate-400 font-num">{new Date(a.at).toLocaleString("en-AU")}</div></div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HrPortal() {
  const [authed, setAuthed] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem(HR_TOKEN_KEY)) { setAuthed(false); return; }
    hrApi.get("/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);
  if (authed === null) return <div className="min-h-screen grid place-items-center bg-slate-900 text-slate-300">Loading…</div>;
  if (!authed) return <HrLogin onOk={() => setAuthed(true)} />;
  return <Portal onLogout={() => setAuthed(false)} />;
}
